<template>
  <b-card title="Предложка изменений Notion">
    <div class="d-flex align-items-center mb-1">
      <b-form-select
          v-model="statusFilter"
          :options="statusOptions"
          style="max-width: 220px"
          @change="loadSuggestions(1)"
      />
      <span v-if="total !== null" class="ml-1 text-muted">Всего: {{ total }}</span>
    </div>

    <b-overlay :show="isLoading" rounded="sm">
      <b-alert v-if="!isLoading && rows.length === 0" show variant="info">
        Предложек нет
      </b-alert>

      <b-table
          v-if="rows.length > 0"
          :items="rows"
          :fields="fields"
          responsive
          striped
          small
          hover
          @row-clicked="openSuggestion"
      >
        <template #cell(type)="data">
          {{ typeLabel(data.item.type) }}
        </template>

        <template #cell(value)="data">
          <b-badge variant="light-primary">{{ data.item.value }}</b-badge>
        </template>

        <template #cell(status)="data">
          <b-badge :variant="statusVariant(data.item.status)">
            {{ statusLabel(data.item.status) }}
          </b-badge>
        </template>

        <template #cell(created_at)="data">
          {{ formatDateTime(data.item.created_at) }}
        </template>
      </b-table>

      <b-pagination
          v-if="total > perPage"
          v-model="currentPage"
          :total-rows="total"
          :per-page="perPage"
          class="mt-1"
          @change="loadSuggestions"
      />
    </b-overlay>
  </b-card>
</template>

<script>
import {
  BCard,
  BTable,
  BOverlay,
  BAlert,
  BBadge,
  BFormSelect,
  BPagination,
} from 'bootstrap-vue'
import { onUnmounted, ref } from '@vue/composition-api'
import store from '@/store'
import router from '@/router'
import contentStoreModule from '@/views/content/contentStoreModule'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { refreshSuggestionBadge } from '@/views/content/suggestionBadge'

export default {
  components: {
    BCard,
    BTable,
    BOverlay,
    BAlert,
    BBadge,
    BFormSelect,
    BPagination,
  },
  setup() {
    const STORE_MODULE_NAME = 'app-content'
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, contentStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const toast = useToast()

    const isLoading = ref(false)
    const rows = ref([])
    const statusFilter = ref('pending')
    const currentPage = ref(1)
    const perPage = ref(50)
    const total = ref(null)

    const statusOptions = [
      { value: 'pending', text: 'Ожидают' },
      { value: 'applied', text: 'Применённые' },
      { value: 'ignored', text: 'Игнорированные' },
      { value: 'all', text: 'Все' },
    ]

    const fields = [
      { key: 'title', label: 'Артист' },
      { key: 'type', label: 'Тип' },
      { key: 'value', label: 'Значение' },
      { key: 'status', label: 'Статус' },
      { key: 'created_at', label: 'Создана' },
    ]

    const statusLabels = { pending: 'Ожидает', applied: 'Применена', ignored: 'Игнорирована' }
    const statusVariants = { pending: 'light-warning', applied: 'light-success', ignored: 'light-secondary' }
    const typeLabels = {
      artist_matrix: 'Матрица артиста',
      artist_letter_count: 'Темы число букв',
      artist_tag: 'Тег артиста',
      artist_country: 'Страна артиста',
      artist_cluster: 'Кластер артиста',
      artist_first_letter: 'Первая буква',
    }

    const statusLabel = status => statusLabels[status] || status
    const statusVariant = status => statusVariants[status] || 'light-secondary'
    const typeLabel = type => typeLabels[type] || type

    const formatDateTime = iso => new Date(iso).toLocaleString('ru-RU', {
      timeZone: 'Europe/Moscow',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

    const showToast = (title, variant) => {
      toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'danger' ? 'AlertTriangleIcon' : 'CheckCircleIcon',
          variant,
        },
      })
    }

    const errorMessage = (error, fallback) => (
      (error.response && error.response.data && error.response.data.message) || fallback
    )

    const loadSuggestions = (page = 1) => {
      isLoading.value = true
      store.dispatch('app-content/fetchSuggestions', {
        status: statusFilter.value,
        page,
        per_page: perPage.value,
      })
          .then(response => {
            rows.value = response.data.data
            total.value = response.data.total
            currentPage.value = response.data.current_page
            perPage.value = response.data.per_page
            refreshSuggestionBadge()
          })
          .catch(error => showToast(errorMessage(error, 'Ошибка загрузки предложек'), 'danger'))
          .finally(() => {
            isLoading.value = false
          })
    }

    const openSuggestion = item => {
      router.push({ name: 'content-suggestion-view', params: { id: item.id } })
    }

    loadSuggestions()

    return {
      isLoading,
      rows,
      fields,
      statusFilter,
      statusOptions,
      currentPage,
      perPage,
      total,
      statusLabel,
      statusVariant,
      typeLabel,
      formatDateTime,
      loadSuggestions,
      openSuggestion,
    }
  },
}
</script>
